import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { contactService } from '../../services/contactService.js';
import { Mail, Eye, Pencil, Trash2 } from 'lucide-react';
import {
  PageLayout,
  DataTable,
  FilterPanel,
  Pagination,
  StatusBadge,
} from '../common/index.js';

const ContactList = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterValues, setFilterValues] = useState({ search: '', replied: '' });
  const navigate = useNavigate();
  const perPage = 10;

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchContacts(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filterValues]);

  useEffect(() => {
    fetchContacts(currentPage);
  }, [currentPage]);

  const fetchContacts = async (page = currentPage) => {
    try {
      setLoading(true);
      const filters = {};
      if (filterValues.search) filters.search = filterValues.search;
      if (filterValues.replied) filters.replied = filterValues.replied;

      const response = await contactService.getAllContacts(page, perPage, filters);
      setContacts(response.data.data || []);
      setTotalPages(response.data.pagination.pages);
    } catch (err) {
      Alert.error('Failed to load contacts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (contactId, contactName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete the contact from "${contactName}"? This action cannot be undone.`,
      'Delete Contact'
    );

    if (confirmed) {
      try {
        await contactService.deleteContact(contactId);
        Alert.success('Contact deleted successfully!');

        if (contacts.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchContacts();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete contact');
      }
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  const columns = [
    {
      key: 'full_name',
      label: 'Customer Name',
      render: (contact) => (
        <button
          onClick={() => navigate(`/contact/view/${contact.id}`)}
          className="font-medium text-brand hover:underline text-left"
        >
          {contact.full_name}
        </button>
      ),
    },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'subject', label: 'Subject' },
    {
      key: 'status',
      label: 'Status',
      render: (contact) => (
        <StatusBadge status={contact.reply ? 'completed' : 'pending'} label={contact.reply ? 'Replied' : 'Pending'} />
      ),
    },
    {
      key: 'created_at',
      label: 'Date',
      render: (contact) => formatDate(contact.created_at),
    },
  ];

  const getActions = (contact) => [
    {
      label: 'View Details',
      icon: Eye,
      onClick: () => navigate(`/contact/view/${contact.id}`),
    },
    {
      label: contact.reply ? 'View Reply' : 'Reply',
      icon: Pencil,
      onClick: () => navigate(`/contact/edit/${contact.id}`),
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: () => handleDelete(contact.id, contact.full_name),
    },
  ];

  return (
    <PageLayout title="Contact Messages" icon={Mail}>
      <FilterPanel
        filters={[
          { key: 'search', label: 'Search', placeholder: 'Name or email...' },
          {
            key: 'replied',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'yes', label: 'Replied' },
              { value: 'no', label: 'Pending' },
            ],
          },
        ]}
        values={filterValues}
        onChange={setFilterValues}
        onClear={() => setFilterValues({ search: '', replied: '' })}
      />

      <DataTable
        columns={columns}
        data={contacts}
        loading={loading}
        emptyMessage="No contacts found"
        rowKey="id"
        actions={getActions}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </PageLayout>
  );
};

export default ContactList;
